import {
  AlertSubtype,
  visibleTypeToSubtypeAlertMap,
} from 'common/src/types/enums/AlertSubtype.enum';
import { AlertType } from 'common/src/types/enums/AlertType.enum';
import { EmailFrequency } from 'common/src/types/enums/EmailFrequency';
import { IUserAlertPreferences } from 'common/src/types/interfaces';

export type SubtypePreferenceState = {
  enabled: boolean;
  frequency: EmailFrequency;
};

export const alertTypes = Object.keys(
  visibleTypeToSubtypeAlertMap,
) as AlertType[];
export const alertSubtypes = Object.values(AlertSubtype);
export const emailFrequencyOptions = Object.values(EmailFrequency);

export const subtypeToTypeMap = alertTypes.reduce(
  (acc, type) => {
    visibleTypeToSubtypeAlertMap[type].forEach((subtype) => {
      acc[subtype] = type;
    });

    return acc;
  },
  {} as Record<AlertSubtype, AlertType>,
);

export const getPreferenceKey = (
  type: AlertType,
  subtype: AlertSubtype,
): string => `${type}::${subtype}`;

export const buildInitialPreferences = (
  alertPreferences: Array<Partial<IUserAlertPreferences>> = [],
): Record<string, SubtypePreferenceState> => {
  const initialState: Record<string, SubtypePreferenceState> = {};

  // Alerts are enabled by default, so a subtype is only disabled when the
  // user has explicitly saved a disabled preference for it.
  alertTypes.forEach((type) => {
    visibleTypeToSubtypeAlertMap[type].forEach((subtype) => {
      initialState[getPreferenceKey(type, subtype)] = {
        enabled: true,
        frequency: EmailFrequency.None,
      };
    });
  });

  alertPreferences.forEach((preference) => {
    if (!preference.subtype || !preference.type) return;
    const key = getPreferenceKey(
      preference.type as AlertType,
      preference.subtype,
    );
    if (!(key in initialState)) return;

    initialState[key] = {
      enabled: preference.enabled,
      frequency: preference.frequency ?? EmailFrequency.None,
    };
  });

  return initialState;
};

export type AlertPreferenceChange = {
  type: AlertType;
  subtype: AlertSubtype;
  enabledBefore: boolean;
  enabledAfter: boolean;
  frequencyBefore: EmailFrequency;
  frequencyAfter: EmailFrequency;
  enabledChanged: boolean;
  frequencyChanged: boolean;
};

export const diffAlertPreferences = (
  original: Array<Partial<IUserAlertPreferences>> = [],
  updated: Array<Partial<IUserAlertPreferences>> = [],
): AlertPreferenceChange[] => {
  const originalState = buildInitialPreferences(original);
  const updatedState = buildInitialPreferences(updated);

  const changes: AlertPreferenceChange[] = [];

  alertTypes.forEach((type) => {
    visibleTypeToSubtypeAlertMap[type].forEach((subtype) => {
      const key = getPreferenceKey(type, subtype);
      const before = originalState[key];
      const after = updatedState[key];

      const enabledChanged = before.enabled !== after.enabled;
      const frequencyChanged = before.frequency !== after.frequency;

      if (!enabledChanged && !frequencyChanged) return;

      changes.push({
        type,
        subtype,
        enabledBefore: before.enabled,
        enabledAfter: after.enabled,
        frequencyBefore: before.frequency,
        frequencyAfter: after.frequency,
        enabledChanged,
        frequencyChanged,
      });
    });
  });

  return changes;
};
